import React, { useState } from 'react';
import { X, User, Lock, Shield, Smartphone, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalToast } from '../../contexts/ToastContext';
import { darkModeClasses } from '../../utils/darkMode';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  role?: 'admin' | 'member' | 'developer';
}

export const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, role = 'admin' }) => {
  const { login, loading, error } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const toast = useGlobalToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const success = await login({ ...formData, role });
      if (success) {
        toast.success('Login successful', `Welcome back!`);
        onClose();
        setFormData({ username: '', password: '' });
      } else {
        toast.error('Login failed', error || 'Invalid credentials');
      }
    } catch (err) {
      toast.error('Login failed', err instanceof Error ? err.message : 'An unexpected error occurred');
    }
  };


  if (!isOpen) return null;

  const isAdmin = role === 'admin';
  const isDeveloper = role === 'developer';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border-primary">
           <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary flex items-center">
             {isAdmin ? (
               <Shield className="h-6 w-6 mr-2 text-blue-600" />
             ) : isDeveloper ? (
               <Shield className="h-6 w-6 mr-2 text-purple-600" />
             ) : (
               <Smartphone className="h-6 w-6 mr-2 text-green-600" />
             )}
             {isAdmin ? 'Admin Login' : isDeveloper ? 'Developer Login' : 'Member Login'}
           </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-red-800 dark:text-red-300 text-sm">
                {error}
                {error.includes('Member account not found') && (
                  <><br /><span className="text-xs opacity-75 mt-1 block">This member account doesn't exist in the database. Please contact your administrator or try a different username.</span></>
                )}
              </p>
            </div>
          )}


          <div>
             <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
               {isAdmin ? 'Admin Username' : isDeveloper ? 'Developer Username' : 'Member ID'}
             </label>
             <div className="relative">
               <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
               <input
                   type="text"
                 value={formData.username}
                 onChange={(e) => setFormData({...formData, username: e.target.value})}
                   className={`w-full ${darkModeClasses.input} rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                 placeholder={isAdmin ? 'Enter admin username' : isDeveloper ? 'Enter developer username' : 'Enter your member ID (e.g., ACC001)'}
                 data-testid="username-input"
                 required
               />
             </div>
           </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                  type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className={`w-full ${darkModeClasses.input} rounded-lg pl-10 pr-10 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="Enter your password"
                data-testid="password-input"
                required
              />
                <button
                  type="button"
                onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 dark:bg-dark-bg-tertiary hover:bg-gray-200 dark:hover:bg-dark-hover-bg text-gray-800 dark:text-dark-text-primary py-2 px-4 rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
               type="submit"
               disabled={loading}
               className={`flex-1 ${isAdmin ? 'bg-blue-600 hover:bg-blue-700' : isDeveloper ? 'bg-purple-600 hover:bg-purple-700' : 'bg-green-600 hover:bg-green-700'} disabled:bg-gray-400 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2`}
               data-testid="submit-login"
             >
               {loading ? (
                 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
               ) : (
                 <>
                   {isAdmin ? <Shield className="h-4 w-4" /> : isDeveloper ? <Shield className="h-4 w-4" /> : <Smartphone className="h-4 w-4" />}
                   <span>Login</span>
                 </>
               )}
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};
